import React, { useMemo } from 'react';

import { SearchSelect } from '@/design-system/search-select';

import { DateField } from '../../components/DateField';
import { EnumSelect, type SelectOption } from '../../components/form';
import { type PickerA11yProps, usePickerA11y } from '../../components/pickerA11y';
import { StorageSelect } from '../../components/StorageSelect';
import { skuLabel, useSkuSearch } from '../../hooks/useCatalog';
import { storagePathOf } from '../../hooks/useStorages';
import { STOCK_TYPE_META, STOCK_TYPES } from '../../status';
import type { WarehouseStockType } from '../../types';
import { type DraftLine } from './opnameDraft';

const STOCK_TYPE_OPTIONS: SelectOption[] = STOCK_TYPES.map((t) => ({
  value: t,
  label: STOCK_TYPE_META[t].label,
}));

/** SKUs listed before the user types (one page; more load on scroll). */
const NEW_BATCH_SKU_PAGE = 100;

const SkuSearch: React.FC<
  PickerA11yProps & {
    clientID: number | null;
    line: DraftLine;
    invalid: boolean;
    testId: string;
    onPick: (patch: Partial<DraftLine>) => void;
  }
> = ({ clientID, line, invalid, testId, onPick, 'aria-label': ariaLabel }) => {
  const ref = usePickerA11y(ariaLabel, invalid);
  // Opens on the client's first active SKUs (up to NEW_BATCH_SKU_PAGE), then
  // narrows as the user types — ops picks from a list, not a blank.
  const search = useSkuSearch(clientID, true, {
    searchOnly: false,
    pageSize: NEW_BATCH_SKU_PAGE,
  });
  const options = useMemo(
    () => search.rows.map((s) => ({ value: s.sku, label: skuLabel(s) })),
    [search.rows]
  );
  return (
    <div ref={ref} className="min-w-0" data-testid={testId}>
      <SearchSelect
        options={options}
        value={line.sku}
        selectedLabelFallback={line.sku ? skuLabel(line) : undefined}
        onChange={(sku) => {
          const picked = search.rows.find((s) => s.sku === sku);
          if (!picked) return;
          onPick({
            sku: picked.sku,
            itemName: picked.itemName,
            uomName: picked.uomName,
          });
          search.setSearch('');
        }}
        onSearchChange={search.setSearch}
        disableLocalFilter
        hasMore={search.hasMore}
        onLoadMore={search.loadMore}
        loading={search.loading}
        placeholder="Cari kode atau nama SKU"
        allowClear={false}
        error={invalid}
        emptyMessage={
          search.error
            ? `Gagal memuat SKU: ${search.error.message}`
            : search.searching
              ? 'SKU tidak ditemukan'
              : 'Client ini belum punya SKU aktif'
        }
      />
    </div>
  );
};

/**
 * The identity fields of a "Batch baru" row — stock found on the shelf that
 * the system has no record of: SKU (searched in the client's active catalog),
 * the storage it was found in, an optional expiry (without one the batch is
 * issued as no-expiry) and the stock type it is booked as (Regular unless it is
 * returned goods). The batch number itself is issued when FAT approves.
 */
export const NewBatchRow: React.FC<{
  hubID: number;
  clientID: number | null;
  line: DraftLine;
  /** 1-based row number, for the accessible names. */
  n: number;
  index: number;
  skuInvalid: boolean;
  storageInvalid: boolean;
  onChange: (patch: Partial<DraftLine>) => void;
}> = ({
  hubID,
  clientID,
  line,
  n,
  index,
  skuInvalid,
  storageInvalid,
  onChange,
}) => (
  <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_160px_120px]">
    <SkuSearch
      aria-label={`SKU baris ${n}`}
      testId={`opname-new-sku-${index}`}
      clientID={clientID}
      line={line}
      invalid={skuInvalid}
      onPick={onChange}
    />
    <StorageSelect
      aria-label={`Storage baris ${n}`}
      testId={`opname-new-storage-${index}`}
      hubID={hubID}
      value={line.storageID}
      onChange={(id, storage) =>
        onChange({
          storageID: id,
          storageName: storage.name,
          storagePath: storagePathOf(storage),
        })
      }
      placeholder="Pilih storage"
      error={storageInvalid}
    />
    <DateField
      aria-label={`Expiry baris ${n}`}
      data-testid={`opname-new-expiry-${index}`}
      value={line.expiredDate}
      onChange={(expiredDate) => onChange({ expiredDate })}
      placeholder="Expiry (opsional)"
    />
    <EnumSelect
      aria-label={`Tipe stok baris ${n}`}
      data-testid={`opname-new-type-${index}`}
      value={line.stockType ?? 'REGULAR'}
      onChange={(stockType) =>
        onChange({ stockType: stockType as WarehouseStockType })
      }
      options={STOCK_TYPE_OPTIONS}
    />
  </div>
);
